/**
 * Tracker home — the real-world half of the coaching loop.
 *
 * "You coach to get results. Your results inform your coach." This screen is
 * the sensor (log + bankroll + stats) AND the launchpad for the coach
 * (warmup / debrief / consult). The heartbeat is the log→debrief flow: after
 * you save a session, the primary CTA is "Debrief with your coach."
 *
 * Logging is FREE (never gated). The coach launches are paid LLM turns, so
 * they route through DrillLaunchLink (pops the upgrade modal in place when
 * walled) — same gating primitive as every drill launch.
 */

import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import type {
  BreakdownDimension,
  LiveSession,
  LogSessionInput,
  SessionFilter,
} from "@/shared/models";
import { getSessionBreakdown, getTrackerHome, logSession } from "@/shared/api";
import {
  consultRoute,
  debriefRoute,
  warmupRoute,
  type SessionContext,
} from "@/shared/chatRoute";
import { DrillLaunchLink } from "@/shared/components/DrillLaunchLink";
import { Flame, BarChart3, NavIcons, Plus, Search, Snowflake, X } from "@/shared/components/icons";
import { BankrollChart } from "@/features/tracker/components/BankrollChart";
import { BreakdownTable } from "@/features/tracker/components/BreakdownTable";
import { SessionForm } from "@/features/tracker/components/SessionForm";
import { SessionRow } from "@/features/tracker/components/SessionRow";
import { StatCards } from "@/features/tracker/components/StatCards";
import { TrackerAchievements } from "@/features/tracker/components/TrackerAchievements";
import { recentDistinct } from "@/features/tracker/lib/trackerFormat";
import { Button, buttonClass } from "@/shared/components/Button";
import { EmptyState } from "@/shared/components/EmptyState";
import { ModalOverlay } from "@/shared/components/ModalOverlay";
import { PageContainer } from "@/shared/components/layout/PageContainer";
import { PageHeader } from "@/shared/components/layout/PageHeader";
import { Link } from "react-router-dom";

type View = "all" | BreakdownDimension;

export function TrackerHome() {
  const queryClient = useQueryClient();

  // Segmentation: "all" (aggregate), or a by-stake/by-location breakdown.
  // `filter` is set when the user taps a breakdown row to drill into one
  // segment — the home payload is then recomputed over just that subset.
  const [view, setView] = useState<View>("all");
  const [filter, setFilter] = useState<SessionFilter | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ["tracker-home", filter],
    queryFn: () => getTrackerHome(filter ?? undefined),
    staleTime: 30_000,
  });

  const showTable = view !== "all" && !filter;
  const breakdownQuery = useQuery({
    queryKey: ["tracker-breakdown", view],
    queryFn: () => getSessionBreakdown(view as BreakdownDimension),
    enabled: showTable,
    staleTime: 30_000,
  });

  function selectView(v: View) {
    setView(v);
    setFilter(null);
  }
  function selectSegment(key: string) {
    setFilter(view === "stake" ? { stake: key } : { location: key });
  }

  // null = closed; "form" = logging; a LiveSession = just-logged success card.
  const [modal, setModal] = useState<"form" | LiveSession | null>(null);

  const logMutation = useMutation({
    mutationFn: (input: LogSessionInput) => logSession(input),
    onSuccess: (session) => {
      queryClient.invalidateQueries({ queryKey: ["tracker-home"] });
      // Logging can unlock tracker achievements (On the Record, Keeping Score,
      // In the Trenches) — refetch so the unlock modal + LevelChip update.
      queryClient.invalidateQueries({ queryKey: ["gamification"] });
      setModal(session); // → cooldown "Debrief" card
    },
  });

  if (isLoading) {
    return (
      <PageContainer width="default">
        <p className="text-sm text-text-muted">Loading your sessions…</p>
      </PageContainer>
    );
  }
  if (isError || !data) {
    return (
      <PageContainer width="default">
        <p className="text-sm text-danger">Couldn't load your tracker.</p>
      </PageContainer>
    );
  }

  const hasSessions = data.sessions.length > 0;
  const completed = data.sessions.filter((s) => s.status === "complete");

  // Quick-pick chips fed by the user's own history (sessions are newest-first).
  const stakeOptions = recentDistinct(
    data.sessions.filter((s) => s.gameType === "cash").map((s) => s.stakes),
  );
  const locationOptions = recentDistinct(data.sessions.map((s) => s.location));

  // The active segment as a coaching context (scopes warmup/consult).
  const segmentContext: SessionContext | undefined = filter?.stake
    ? { label: filter.stake, stake: filter.stake }
    : filter?.location
      ? { label: filter.location, location: filter.location }
      : undefined;

  return (
    <PageContainer width="default">
      {/* Header */}
      <PageHeader
        Icon={NavIcons.tracker}
        title="Tracker"
        subtitle="Coach to get results. Your results inform your coach."
        action={
          <Button size="sm" onClick={() => setModal("form")}>
            <Plus size={16} strokeWidth={2.25} />
            Log
          </Button>
        }
      />

      {/* Open warmup stub → finish it */}
      {data.openLiveSession && (
        <DrillLaunchLink
          to={`/tracker/${data.openLiveSession.id}`}
          className="block rounded-2xl border border-accent/40 bg-accent/10 px-4 py-3"
        >
          <div className="text-sm font-medium text-text">A session is in progress</div>
          <div className="mt-0.5 text-xs text-text-muted">
            {data.openLiveSession.intention
              ? `“${data.openLiveSession.intention}” — finish & debrief when you're done.`
              : "Finish logging it when you're done playing."}
          </div>
        </DrillLaunchLink>
      )}

      {hasSessions ? (
        <>
          <SegmentControl view={view} onChange={selectView} />

          {showTable ? (
            <BreakdownTable
              segments={breakdownQuery.data?.segments ?? []}
              loading={breakdownQuery.isLoading}
              onSelect={selectSegment}
            />
          ) : (
            <>
              {filter && (
                <div className="flex items-center gap-2 px-1">
                  <span className="text-xs text-text-muted">Showing</span>
                  <button
                    type="button"
                    onClick={() => setFilter(null)}
                    className="inline-flex items-center gap-1 rounded-full border border-accent/40 bg-accent/10 px-2.5 py-1 text-xs font-medium text-text transition hover:bg-accent/15"
                  >
                    {filter.stake ?? filter.location}
                    <X size={12} strokeWidth={2.25} />
                  </button>
                </div>
              )}

              {/* Bankroll + stats (aggregate, or for the filtered segment) */}
              <section className="rounded-2xl border border-border bg-surface p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <h2 className="text-sm font-semibold text-text">Bankroll</h2>
                  <span className="text-xs text-text-muted">
                    {data.stats.sessionCount} sessions
                  </span>
                </div>
                <BankrollChart series={data.bankroll} />
              </section>

              <StatCards stats={data.stats} />

              {/* Coach launches — context-aware when filtered to a segment
                  (the coach is briefed on the stake/location). */}
              <CoachRow context={segmentContext} />

              {/* Close the loop — the leak report blends your real sessions */}
              <Link
                to="/skills/focus"
                className="flex items-center gap-3 rounded-2xl border border-border bg-surface p-4 transition hover:bg-text/5"
              >
                <div className="shrink-0 w-9 h-9 rounded-full bg-accent/15 flex items-center justify-center">
                  <Search size={18} strokeWidth={1.75} className="text-accent" />
                </div>
                <div className="min-w-0">
                  <div className="text-sm font-medium text-text">Turn results into reps</div>
                  <div className="text-xs text-text-muted">
                    Your leak report blends what your real sessions reveal — drill those leaks.
                  </div>
                </div>
              </Link>

              {/* Lifetime badges — your overall progress */}
              <TrackerAchievements />

              {/* History (filtered to the segment when drilled in) */}
              <section className="space-y-2">
                <h2 className="text-sm font-semibold text-text px-1">
                  {filter ? "Sessions" : "Recent sessions"}
                </h2>
                <div className="space-y-2">
                  {data.sessions.map((s) => (
                    <SessionRow key={s.id} session={s} />
                  ))}
                </div>
                {completed.length === 0 && (
                  <p className="text-xs text-text-muted px-1">
                    No completed sessions yet — finish the one in progress to see your stats.
                  </p>
                )}
              </section>
            </>
          )}
        </>
      ) : (
        <TrackerEmptyState onLog={() => setModal("form")} />
      )}

      {/* Log form modal */}
      {modal === "form" && (
        <ModalOverlay title="Log a session" onClose={() => setModal(null)}>
          <SessionForm
            submitting={logMutation.isPending}
            error={logMutation.isError ? "Couldn't save — try again." : null}
            stakeOptions={stakeOptions}
            locationOptions={locationOptions}
            onSubmit={(input) => logMutation.mutate(input)}
            onCancel={() => setModal(null)}
          />
        </ModalOverlay>
      )}

      {/* Cooldown success → debrief CTA (the loop's heartbeat) */}
      {modal && modal !== "form" && (
        <ModalOverlay title="Session saved ✓" onClose={() => setModal(null)}>
          <div className="space-y-4">
            <p className="text-sm text-text-muted">
              Logged. Now turn it into improvement — debrief the session with your
              coach to find the leaks worth drilling.
            </p>
            <DrillLaunchLink
              to={debriefRoute(modal.id)}
              className={buttonClass({ variant: "primary", fullWidth: true })}
            >
              <Snowflake size={16} strokeWidth={2} />
              Debrief with your coach
            </DrillLaunchLink>
            <Button variant="ghost" fullWidth onClick={() => setModal(null)}>
              Maybe later
            </Button>
          </div>
        </ModalOverlay>
      )}
    </PageContainer>
  );
}

const SEGMENTS: { value: View; label: string }[] = [
  { value: "all", label: "All" },
  { value: "stake", label: "By stake" },
  { value: "location", label: "By location" },
];

function SegmentControl({
  view,
  onChange,
}: {
  view: View;
  onChange: (v: View) => void;
}) {
  return (
    <div className="inline-flex rounded-lg border border-border bg-surface p-0.5 text-sm">
      {SEGMENTS.map((s) => (
        <button
          key={s.value}
          type="button"
          onClick={() => onChange(s.value)}
          className={[
            "px-3 py-1.5 rounded-md transition",
            view === s.value
              ? "bg-accent/15 text-text font-medium"
              : "text-text-muted hover:text-text",
          ].join(" ")}
        >
          {s.label}
        </button>
      ))}
    </div>
  );
}

function CoachRow({ context }: { context?: SessionContext }) {
  // When scoped to a segment, the coach is briefed on it (kickoff + scoped
  // SESSION FACTS) — "warm me up for my 1/2 game", not the whole history.
  const scoped = context ? ` ${context.label}` : "";
  return (
    <section className="grid grid-cols-2 gap-2.5">
      <DrillLaunchLink
        to={warmupRoute(context)}
        className="flex flex-col gap-1 rounded-2xl border border-border bg-surface p-4 transition hover:bg-text/5"
      >
        <Flame size={20} strokeWidth={1.75} className="text-brand" />
        <span className="text-sm font-medium text-text">Warm up{scoped}</span>
        <span className="text-xs text-text-muted">
          Set an intention before you play{scoped ? ` ${context!.label}` : ""}.
        </span>
      </DrillLaunchLink>
      <DrillLaunchLink
        to={consultRoute(context)}
        className="flex flex-col gap-1 rounded-2xl border border-border bg-surface p-4 transition hover:bg-text/5"
      >
        <BarChart3 size={20} strokeWidth={1.75} className="text-accent" />
        <span className="text-sm font-medium text-text">Consult coach</span>
        <span className="text-xs text-text-muted">
          {context ? `Talk ${context.label} bankroll, trends, leaks.` : "Talk bankroll, trends, what to fix."}
        </span>
      </DrillLaunchLink>
    </section>
  );
}

function TrackerEmptyState({ onLog }: { onLog: () => void }) {
  return (
    <EmptyState
      Icon={Flame}
      title="Close the loop"
      message="Drills build your game; your sessions prove it. Log a real session and your coach can debrief it, find the leaks that actually cost you, and send you to the right drills. Logging is free and takes 15 seconds."
      action={
        <Button onClick={onLog}>
          <Plus size={16} strokeWidth={2.25} />
          Log your first session
        </Button>
      }
    />
  );
}
